import React, { useEffect, useMemo, useState } from "react";
import {
  Coins,
  ShieldCheck,
  AlertTriangle,
  RefreshCw,
  Copy,
  Check,
  ExternalLink,
  Sparkles,
  TrendingUp,
  Hash,
  Layers,
  Send,
  ArrowDownLeft,
  ArrowUpRight,
  X,
  Inbox,
  Zap,
  HelpCircle,
  User as UserIcon,
  Globe,
  Wrench,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import SolanaWithdrawCard from "../components/aegis/SolanaWithdrawCard";
import {
  getAegisSupply,
  getAegisWallet,
  verifyAegisChain,
  transferAegis,
  quickTransferAegis,
  getAegisTransfers,
  architectRepairAegisChain,
} from "../lib/api";
import LiveTicker from "../components/aegis/LiveTicker";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";

function shortHash(h, n = 12) {
  if (!h) return "—";
  if (h.length <= n * 2 + 1) return h;
  return `${h.slice(0, n)}…${h.slice(-6)}`;
}

function formatProof(n) {
  if (n == null) return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  if (x >= 1_000_000) return `${(x / 1_000_000).toFixed(2)}M`;
  if (x >= 1_000) return `${(x / 1_000).toFixed(1)}k`;
  return String(Math.round(x));
}

function formatApt(n) {
  if (n == null) return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  if (x >= 1_000_000) return `${(x / 1_000_000).toFixed(2)}M`;
  if (x >= 1_000) return `${(x / 1_000).toFixed(2)}k`;
  // sub-thousand: 2 decimals so 7.50 APT reads clean
  return x.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatVestingCountdown(iso) {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (!Number.isFinite(target)) return null;
  const ms = target - Date.now();
  if (ms <= 0) return null;
  const days = Math.floor(ms / 86_400_000);
  if (days >= 2) return `${days} days`;
  if (days === 1) return "1 day";
  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 2) return `${hours} hrs`;
  if (hours === 1) return "1 hr";
  const mins = Math.max(1, Math.floor(ms / 60_000));
  return `${mins} min`;
}

function CopyHash({ value, label = "hash", testid }) {
  const [done, setDone] = useState(false);
  if (!value) return <span className="text-zinc-600 font-mono text-[11px]">—</span>;
  return (
    <button
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(value);
          setDone(true);
          toast.success(`${label} copied`);
          setTimeout(() => setDone(false), 1200);
        } catch {
          toast.error("Clipboard unavailable");
        }
      }}
      data-testid={testid}
      title={`Copy full ${label}`}
      className="group font-mono text-[11px] text-zinc-300 hover:text-amber-300 inline-flex items-center gap-1.5 border border-zinc-800 hover:border-amber-500/50 px-1.5 py-0.5 transition-colors"
    >
      {done ? <Check className="h-3 w-3 text-amber-400" /> : <Copy className="h-3 w-3 opacity-50 group-hover:opacity-100" />}
      {shortHash(value)}
    </button>
  );
}

function TokenIcon({ size = 48, className = "" }) {
  return (
    <img
      src="/brand/aegis-token-icon.png"
      alt="AEGIS"
      width={size}
      height={size}
      className={`shrink-0 object-cover ${className}`}
      style={{
        filter: "drop-shadow(0 0 12px rgba(251,191,36,0.25))",
      }}
      data-testid="aegis-token-icon"
    />
  );
}

function CoinRow({ coin, index, selected, onToggle, currentUserId, isArchitect }) {
  const isGenesis = !coin.prev_coin_hash;
  const [showAudit, setShowAudit] = useState(false);
  const isTruth = coin.source?.block_type === "truth";
  const isMine = coin.current_owner_user_id === currentUserId || isArchitect;
  const wasDiscovered = coin.recipient_user_id === currentUserId;
  const vestingCountdown = formatVestingCountdown(coin.vesting_unlocks_at);
  return (
    <article
      className={`border bg-[#0b0b0d] transition-colors ${
        selected
          ? "border-amber-400 bg-amber-500/5"
          : isTruth ? "border-emerald-500/30 hover:border-emerald-500/60" : "border-rose-500/30 hover:border-rose-500/60"
      }`}
      data-testid={`aegis-coin-${index}`}
    >
      <div className="p-3 sm:p-4">
        <div className="flex items-start gap-3">
          {isMine && (
            <input
              type="checkbox"
              checked={selected}
              onChange={() => onToggle(coin.id)}
              data-testid={`coin-select-${index}`}
              className="mt-1.5 accent-amber-400"
            />
          )}
          <div className={`shrink-0 h-14 w-14 border ${isTruth ? "border-emerald-500/50" : "border-rose-500/50"} flex items-center justify-center bg-black overflow-hidden`}>
            <img
              src="/brand/aegis-token-icon.png"
              alt="AEGIS"
              className="w-full h-full object-cover"
              style={{ filter: isTruth ? "saturate(1.1)" : "hue-rotate(-20deg) saturate(1.2)" }}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border ${isTruth ? "border-emerald-500/40 text-emerald-300" : "border-rose-500/40 text-rose-300"}`}>
                {isTruth ? "Truth" : "Refuted"} · Block #{coin.source?.block_height}
              </span>
              {isGenesis && (
                <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border border-amber-500/40 text-amber-300 bg-amber-500/5">
                  ★ Genesis
                </span>
              )}
              {!wasDiscovered && isMine && (
                <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border border-sky-500/40 text-sky-300 inline-flex items-center gap-1">
                  <ArrowDownLeft className="h-3 w-3" /> received
                </span>
              )}
              {vestingCountdown && (
                <span
                  className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border border-sky-500/40 text-sky-300 bg-sky-500/5 inline-flex items-center gap-1 font-mono"
                  title={`Verifier-payout cliff · SPL withdraw unlocks in ${vestingCountdown} (${coin.vesting_unlocks_at}). P2P transfers still work.`}
                  data-testid={`coin-vesting-${index}`}
                >
                  <Clock className="h-3 w-3" /> unlocks · {vestingCountdown}
                </span>
              )}
              {coin.value_in_apt > 0 && (
                <span
                  className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border border-amber-500/40 text-amber-300 bg-amber-500/5 font-mono"
                  title={`${coin.value_in_apt} APT · backed by ${coin.value_in_proof?.toLocaleString?.() || 0} PROOF (saved LLM tokens)`}
                  data-testid={`coin-apt-${index}`}
                >
                  ◆ {Number(coin.value_in_apt).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} APT
                </span>
              )}
              {!coin.value_in_apt && coin.value_in_proof > 0 && (
                <span
                  className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border border-amber-500/40 text-amber-300 bg-amber-500/5 font-mono"
                  title={`${coin.value_in_proof.toLocaleString()} PROOF — LLM tokens this verified claim will save the network`}
                  data-testid={`coin-proof-${index}`}
                >
                  ◆ {formatProof(coin.value_in_proof)} PROOF
                </span>
              )}
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono ml-auto">
                {coin.minted_at ? new Date(coin.minted_at).toISOString().slice(0, 19).replace("T", " ") : ""}
              </span>
            </div>
            {/* Default view: just the coin hash — that's all you need
                to send, receive, or look up a coin. The chain-link
                (prev) and claim hash live on the same record and
                cryptographically anchor the receipt whether or not we
                render them; they're audit-only. The "audit" toggle
                below reveals them for power users / auditors. */}
            <div className="mt-2 flex items-center gap-1 text-[10.5px] font-mono">
              <span className="text-zinc-500 uppercase tracking-[0.25em] text-[9px]">coin</span>
              <CopyHash value={coin.coin_hash} label="coin hash" testid={`coin-hash-${index}`} />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowAudit(s => !s); }}
                data-testid={`coin-audit-toggle-${index}`}
                className="ml-auto text-[9px] uppercase tracking-[0.25em] text-zinc-600 hover:text-amber-300 font-mono"
              >
                {showAudit ? "− audit" : "+ audit"}
              </button>
            </div>
            {showAudit && (
              <div className="mt-2 grid sm:grid-cols-2 gap-2 text-[10.5px] font-mono border-l border-zinc-800 pl-2" data-testid={`coin-audit-${index}`}>
                <div className="flex items-center gap-1">
                  <span className="text-zinc-500 uppercase tracking-[0.25em] text-[9px]">prev</span>
                  {isGenesis ? (
                    <span className="text-amber-400 text-[10px] uppercase tracking-[0.25em]">∅ Genesis</span>
                  ) : (
                    <CopyHash value={coin.prev_coin_hash} label="previous hash" />
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-zinc-500 uppercase tracking-[0.25em] text-[9px]">claim</span>
                  <CopyHash value={coin.source?.claim_sha256} label="claim hash" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

function TransferRow({ transfer, currentUserId, isArchitect }) {
  const outgoing = transfer.from_user_id === currentUserId;
  const incoming = transfer.to_user_id === currentUserId;
  return (
    <div className="border border-zinc-800 bg-[#0b0b0d] p-3 flex items-start gap-3" data-testid={`transfer-row-${transfer.id}`}>
      <div className={`shrink-0 h-9 w-9 border flex items-center justify-center ${
        outgoing ? "border-rose-500/50 text-rose-300" :
        incoming ? "border-emerald-500/50 text-emerald-300" :
        "border-zinc-700 text-zinc-300"
      }`}>
        {outgoing ? <ArrowUpRight className="h-4 w-4" /> : incoming ? <ArrowDownLeft className="h-4 w-4" /> : <Send className="h-4 w-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border ${
            outgoing ? "border-rose-500/40 text-rose-300" :
            incoming ? "border-emerald-500/40 text-emerald-300" :
            "border-zinc-700 text-zinc-300"
          }`}>
            {outgoing ? "sent" : incoming ? "received" : "transfer"} · 1.0 AEGIS
          </span>
          <span className="text-[10.5px] text-zinc-400 font-mono">
            {outgoing ? "→" : "←"} {isArchitect ? (outgoing ? transfer.to_user_id : transfer.from_user_id) : (outgoing ? transfer.to_user_id : transfer.from_user_id).slice(0, 8) + "…"}
          </span>
          <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
            {transfer.transferred_at ? new Date(transfer.transferred_at).toISOString().slice(0, 19).replace("T", " ") : ""}
          </span>
        </div>
        {transfer.memo && (
          <div className="mt-1 text-[12px] text-zinc-300 italic">&ldquo;{transfer.memo}&rdquo;</div>
        )}
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[10px] font-mono text-zinc-500">
          <span>coin · <span className="text-zinc-300">{(transfer.coin_id || "").slice(0, 8)}</span></span>
          <span>tx · <span className="text-zinc-300">{(transfer.transfer_hash || "").slice(0, 14)}…</span></span>
        </div>
      </div>
    </div>
  );
}

function QuickSendCard({ wallet, isArchitect, onSent }) {
  const [recipient, setRecipient] = useState("");
  const [count, setCount] = useState(1);
  const [memo, setMemo] = useState("");
  const [sending, setSending] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const tradeable = wallet?.tradeable_count ?? 0;
  const personalScope = wallet?.scope === "personal" || wallet?.scope === "self";

  const onSend = async (e) => {
    e?.preventDefault?.();
    const n = Math.max(1, Math.min(Number(count) || 0, tradeable));
    if (n <= 0) {
      toast.error("You have no tradeable AEGIS to send.");
      return;
    }
    const r = recipient.trim();
    if (!r) {
      toast.error("Enter a @username or wallet ID.");
      return;
    }
    setSending(true);
    try {
      // If it starts with '@' (or has no dashes / underscores typical
      // of a wallet_id), treat as a username. Otherwise treat as a
      // raw wallet_id. This is forgiving — users can paste either.
      const looksLikeUsername =
        r.startsWith("@") || /^[a-zA-Z0-9_]{3,30}$/.test(r);
      const args = looksLikeUsername
        ? { coin_count: n, to_username: r.replace(/^@/, ""), memo: memo || null }
        : { coin_count: n, to_user_id: r, memo: memo || null };
      const res = await quickTransferAegis(args);
      toast.success(`Sent ${res.transferred} AEGIS to ${r}`);
      setRecipient("");
      setMemo("");
      setCount(1);
      onSent?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : detail?.message || err?.message || "Transfer failed"
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border border-amber-500/50 bg-amber-500/[0.04] p-5" data-testid="quick-send-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 flex items-center gap-2">
          <Zap className="h-3.5 w-3.5" /> Quick send AEGIS
        </div>
        <button
          type="button"
          onClick={() => setHelpOpen((v) => !v)}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-amber-300"
          data-testid="quick-send-help-toggle"
        >
          <HelpCircle className="h-3 w-3" /> {helpOpen ? "hide help" : "how it works"}
        </button>
      </div>

      {helpOpen && (
        <div className="border border-zinc-800 bg-black/40 p-3 text-[11px] text-zinc-400 leading-relaxed mb-4 font-mono" data-testid="quick-send-help">
          <p className="mb-2">
            <span className="text-amber-300">1.</span> You have{" "}
            <span className="text-emerald-300">{tradeable}</span> tradeable
            AEGIS coins.
          </p>
          <p className="mb-2">
            <span className="text-amber-300">2.</span> Paste a{" "}
            <span className="text-amber-300">@username</span> (preferred) or
            raw wallet ID. Emails are NOT supported — wallet-to-wallet only.
          </p>
          <p>
            <span className="text-amber-300">3.</span> Hit send. The transfer
            is HMAC-signed and links into the AEGIS hash chain instantly.
          </p>
        </div>
      )}

      <form onSubmit={onSend} className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
        <input
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          placeholder="@username or wallet ID"
          type="text"
          data-testid="quick-send-recipient"
          className="w-full bg-[#08090c] border border-zinc-800 focus:border-amber-500 px-3 py-2 text-sm text-zinc-100 outline-none font-mono"
        />
        <div className="flex gap-2">
          <input
            value={count}
            onChange={(e) => setCount(e.target.value.replace(/[^0-9]/g, ""))}
            type="text"
            inputMode="numeric"
            min={1}
            max={tradeable}
            placeholder="amount"
            data-testid="quick-send-amount"
            className="w-24 bg-[#08090c] border border-zinc-800 focus:border-amber-500 px-3 py-2 text-sm text-zinc-100 outline-none font-mono text-center"
          />
          <button
            type="button"
            onClick={() => setCount(tradeable)}
            disabled={tradeable === 0}
            data-testid="quick-send-max"
            className="px-2 text-[10px] uppercase tracking-[0.2em] border border-zinc-800 text-zinc-400 hover:text-amber-300 hover:border-amber-500/40 font-mono disabled:opacity-40"
          >
            max
          </button>
        </div>
        <input
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          placeholder="memo (optional)"
          maxLength={240}
          data-testid="quick-send-memo"
          className="sm:col-span-2 bg-[#08090c] border border-zinc-800 focus:border-amber-500 px-3 py-2 text-sm text-zinc-100 outline-none font-mono"
        />
        <button
          type="submit"
          disabled={sending || tradeable === 0 || !recipient.trim()}
          data-testid="quick-send-submit"
          className="sm:col-span-2 inline-flex items-center justify-center gap-2 px-4 py-3 text-[11px] uppercase tracking-[0.25em] border border-amber-500/60 text-amber-200 bg-amber-500/10 hover:bg-amber-500/20 disabled:opacity-40 disabled:cursor-not-allowed font-mono"
        >
          <Send className="h-3.5 w-3.5" />
          {sending
            ? "Signing & sending…"
            : `Send ${Math.max(1, Math.min(Number(count) || 0, tradeable))} AEGIS`}
        </button>
        {!personalScope && isArchitect && (
          <div className="sm:col-span-2 text-[10px] uppercase tracking-[0.2em] text-amber-400/70 font-mono flex items-center gap-1.5">
            <AlertTriangle className="h-3 w-3" />
            Architect chain view — quick send always operates on your PERSONAL coins, regardless of scope.
          </div>
        )}
      </form>
    </div>
  );
}


function SendPanel({ selectedIds, onClear, onSent }) {
  const [recipientEmail, setRecipientEmail] = useState("");
  const [memo, setMemo] = useState("");
  const [sending, setSending] = useState(false);

  const onSend = async (e) => {
    e?.preventDefault?.();
    if (!recipientEmail.trim() || selectedIds.length === 0) return;
    setSending(true);
    try {
      const res = await transferAegis({
        coin_ids: selectedIds,
        to_email: recipientEmail.trim(),
        memo: memo || null,
      });
      toast.success(`Sent ${res.transferred} AEGIS to ${recipientEmail}`);
      setRecipientEmail("");
      setMemo("");
      onClear();
      onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transfer failed");
    } finally {
      setSending(false);
    }
  };

  if (selectedIds.length === 0) {
    return (
      <div className="border border-dashed border-zinc-800 bg-[#0b0b0d] p-4 text-center text-[11px] uppercase tracking-[0.25em] text-zinc-500" data-testid="send-empty-hint">
        <Send className="h-4 w-4 inline mr-2 opacity-50" />
        Select coins below to send
      </div>
    );
  }

  return (
    <form onSubmit={onSend} className="border border-amber-500/40 bg-amber-500/5 p-4" data-testid="send-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 flex items-center gap-2">
          <Send className="h-3 w-3" />
          Send {selectedIds.length} AEGIS
        </div>
        <button
          type="button"
          onClick={onClear}
          className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-zinc-100 inline-flex items-center gap-1"
          data-testid="send-clear"
        >
          <X className="h-3 w-3" /> clear
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input
          value={recipientEmail}
          onChange={(e) => setRecipientEmail(e.target.value)}
          placeholder="recipient email"
          type="email"
          data-testid="send-recipient-email"
          className="bg-[#08090c] border border-zinc-800 focus:border-amber-500 px-3 py-2 text-sm text-zinc-100 outline-none font-mono"
        />
        <input
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          placeholder="memo (optional)"
          maxLength={240}
          data-testid="send-memo"
          className="bg-[#08090c] border border-zinc-800 focus:border-amber-500 px-3 py-2 text-sm text-zinc-100 outline-none font-mono"
        />
      </div>
      <button
        type="submit"
        disabled={sending || !recipientEmail.trim()}
        data-testid="send-confirm"
        className="mt-3 inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-[0.2em] border border-amber-500/60 text-amber-200 bg-amber-500/10 hover:bg-amber-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Send className="h-3.5 w-3.5" />
        {sending ? "Signing & sending…" : `Send ${selectedIds.length} AEGIS`}
      </button>
    </form>
  );
}

export default function Wallet() {
  const { user } = useAuth();
  const [supply, setSupply] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [chain, setChain] = useState(null);
  const [transfers, setTransfers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [selected, setSelected] = useState([]);
  const [showTransfers, setShowTransfers] = useState(false);
  const [quickSendOpen, setQuickSendOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  // Architect can flip between chain-master view (the entire ledger) and
  // their PERSONAL holdings (just their 9% treasury slice + received
  // transfers). Default 'auto' which the backend resolves to 'architect'
  // for is_architect=true. Non-architects always show 'self'.
  const [scope, setScope] = useState("auto");
  const isArchitect = !!user?.is_architect;
  const [repairingChain, setRepairingChain] = useState(false);

  const repairChain = async () => {
    if (repairingChain) return;
    setRepairingChain(true);
    toast.info("Re-signing broken links with architect HMAC secret…");
    try {
      const res = await architectRepairAegisChain();
      const after = res?.after;
      if (after?.valid) {
        toast.success(
          `Chain healed · patched ${res.patched} / ${res.total_coins} coins. ✓ verified ${after.verified}`
        );
      } else {
        toast.error(
          `Repair finished but chain still broken: ${after?.reason || "unknown"}`
        );
      }
      setRefreshTick((t) => t + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Repair failed");
    } finally {
      setRepairingChain(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      getAegisSupply(),
      getAegisWallet(100, scope),
      verifyAegisChain(),
      getAegisTransfers(50).catch(() => ({ transfers: [] })),
    ])
      .then(([s, w, c, t]) => {
        if (cancelled) return;
        setSupply(s);
        setWallet(w);
        setChain(c);
        setTransfers(t);
      })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshTick, scope]);

  const coins = wallet?.coins || [];

  const breakdown = useMemo(() => {
    const truth = coins.filter((c) => (c.source || {}).block_type === "truth").length;
    const refuted = coins.filter((c) => (c.source || {}).block_type === "refuted").length;
    return { truth, refuted };
  }, [coins]);

  const toggleSelect = (coinId) => {
    setSelected((s) => s.includes(coinId) ? s.filter((x) => x !== coinId) : [...s, coinId]);
  };

  return (
    <div className="min-h-[calc(100vh-130px)] bg-[#09090b] text-zinc-100">
      <LiveTicker fallbackMultiplier={supply?.burn_multiplier} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-1 inline-flex items-center gap-2">
              <Coins className="h-3 w-3" /> △ AEGIS · proof-of-cognitive-labor
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-[0.15em] text-zinc-100">
              Wallet
            </h1>
            <p className="text-zinc-400 text-sm mt-2 max-w-2xl leading-relaxed">
              Every verified fact mints an{" "}
              <span className="text-amber-300 font-mono">AEGIS</span> coin.
              Send them, receive them, or withdraw to Solana. Full economics
              in{" "}
              <a href="/thesis" className="text-amber-300 underline decoration-dotted hover:text-amber-200">
                /thesis
              </a>
              .
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowTransfers((s) => !s)}
              data-testid="toggle-transfers-view"
              className={`inline-flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.2em] border ${showTransfers ? "border-amber-500/60 text-amber-300 bg-amber-500/5" : "border-zinc-800 text-zinc-400 hover:border-amber-500/50"}`}
            >
              <Inbox className="h-3.5 w-3.5" />
              Transfers {transfers?.count ? `(${transfers.count})` : ""}
            </button>
            <button
              onClick={() => setRefreshTick((t) => t + 1)}
              data-testid="wallet-refresh"
              className="inline-flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.2em] border border-zinc-800 text-zinc-400 hover:border-amber-500/60 hover:text-amber-300"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Hero balance */}
        <div className="grid sm:grid-cols-3 gap-2 mb-6">
          <div
            className="sm:col-span-2 relative border border-amber-500/30 overflow-hidden"
            data-testid="wallet-balance"
          >
            {/* Token art as background wallpaper, heavily dimmed */}
            <div
              aria-hidden="true"
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: "url('/brand/aegis-token.jpg')",
                backgroundSize: "cover",
                backgroundPosition: "center",
                opacity: 0.18,
                filter: "blur(2px) saturate(1.2)",
              }}
            />
            <div
              aria-hidden="true"
              className="absolute inset-0 pointer-events-none bg-gradient-to-br from-amber-500/10 via-[#0b0b0d]/80 to-[#0b0b0d]"
            />
            <div className="relative p-5 sm:p-6 flex items-start gap-4 sm:gap-5">
              <TokenIcon size={84} className="hidden sm:block" />
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 flex items-center gap-1.5">
                    <Sparkles className="h-3 w-3" /> AEGIS · the scarce tradeable token
                  </div>
                  {isArchitect && (
                    <div
                      className="flex border border-zinc-800 overflow-hidden"
                      data-testid="wallet-scope-toggle"
                      title="Architect view selector: chain-wide totals vs your personal balance"
                    >
                      <button
                        type="button"
                        onClick={() => setScope("auto")}
                        data-testid="wallet-scope-chain"
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] font-mono ${scope !== "personal" ? "bg-amber-500/15 text-amber-200" : "text-zinc-500 hover:text-zinc-300"}`}
                      >
                        <Globe className="h-3 w-3" /> chain
                      </button>
                      <button
                        type="button"
                        onClick={() => setScope("personal")}
                        data-testid="wallet-scope-personal"
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] font-mono border-l border-zinc-800 ${scope === "personal" ? "bg-amber-500/15 text-amber-200" : "text-zinc-500 hover:text-zinc-300"}`}
                      >
                        <UserIcon className="h-3 w-3" /> me
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <div
                    className="font-mono text-5xl sm:text-6xl text-amber-300 leading-none drop-shadow-[0_0_18px_rgba(251,191,36,0.35)]"
                    data-testid="wallet-balance-apt"
                    title={`${(wallet?.total_apt ?? 0).toLocaleString()} APT · backed by ${(wallet?.total_proof ?? 0).toLocaleString()} PROOF (saved LLM tokens)`}
                  >
                    {wallet
                      ? Number(wallet.total_apt ?? 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "…"}
                  </div>
                  <div className="font-mono text-2xl text-amber-400/70">APT</div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 ml-2">
                    1 APT = 1k PROOF
                  </div>
                </div>
                <div
                  className="mt-1 font-mono text-[11px] uppercase tracking-[0.25em] text-amber-200/60"
                  data-testid="wallet-balance-proof"
                  title={`${(wallet?.total_proof ?? 0).toLocaleString()} LLM tokens this wallet has saved the network forever`}
                >
                  backed by ◆ {wallet ? (wallet.total_proof ?? 0).toLocaleString() : "…"} PROOF saved
                </div>
                {/* v19.40.8 · Spendable-right-now line so the wallet page
                    and the console pop-out always show the same number.
                    If any AEGIS has been converted to console credits,
                    surface that too — no missing-balance illusion. */}
                {wallet && (
                  <div
                    className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[10px] uppercase tracking-[0.22em]"
                    data-testid="wallet-spendable-row"
                  >
                    <span className="text-emerald-300">
                      ✓ {Number(wallet.spendable_apt ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} spendable
                    </span>
                    {(wallet.spent_to_credits_apt ?? 0) > 0 && (
                      <span
                        className="text-zinc-500"
                        title="Total AEGIS you've converted into console credits via the Wallet pop-out."
                      >
                        · {Number(wallet.spent_to_credits_apt).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} spent on console
                      </span>
                    )}
                  </div>
                )}
                {/* Receive-address row — usernames are public handles
                    that act as your wallet address. The wallet_id
                    fallback covers users who haven't claimed one yet. */}
                <div
                  className="mt-3 border border-zinc-800 bg-black/40 p-2 sm:p-3 flex items-center gap-2 group"
                  data-testid="wallet-address-row"
                >
                  <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 shrink-0 hidden sm:block">
                    Your handle →
                  </div>
                  <span
                    className="flex-1 font-mono text-[11px] sm:text-xs text-amber-200 break-all truncate"
                    data-testid="wallet-address-value"
                    title={wallet?.username ? `@${wallet.username}` : (wallet?.user_id || "")}
                  >
                    {wallet?.username ? `@${wallet.username}` : (wallet?.user_id || "—")}
                  </span>
                  {!wallet?.username && wallet?.user_id && (
                    <button
                      type="button"
                      data-testid="wallet-claim-username"
                      onClick={async () => {
                        const desired = window.prompt(
                          "Claim a public handle (3-30 chars, lowercase letters / numbers / underscore). Example: sirchewyyy"
                        );
                        if (!desired) return;
                        try {
                          const { claimUsername } = await import("../lib/api");
                          const res = await claimUsername(desired);
                          toast.success(`Handle claimed: @${res.username}`);
                          setRefreshTick((t) => t + 1);
                        } catch (err) {
                          const d = err?.response?.data?.detail;
                          toast.error(typeof d === "string" ? d : (d?.message || "Could not claim"));
                        }
                      }}
                      className="inline-flex items-center gap-1.5 px-2 py-1 border border-amber-500/60 hover:border-amber-300 text-amber-300 hover:text-amber-200 text-[10px] uppercase tracking-[0.2em] font-mono shrink-0"
                    >
                      <Sparkles className="h-3 w-3" /> claim
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid="wallet-address-copy"
                    onClick={async () => {
                      try {
                        const v = wallet?.username
                          ? `@${wallet.username}`
                          : (wallet?.user_id || "");
                        await navigator.clipboard.writeText(v);
                        toast.success("Handle copied · share to receive AEGIS");
                      } catch {
                        toast.error("Clipboard unavailable");
                      }
                    }}
                    className="inline-flex items-center gap-1.5 px-2 py-1 border border-zinc-800 hover:border-amber-500/60 text-zinc-400 hover:text-amber-300 text-[10px] uppercase tracking-[0.2em] font-mono shrink-0"
                  >
                    <Copy className="h-3 w-3" /> copy
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap gap-3 text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-mono">
                  <span
                    data-testid="wallet-tradeable-summary"
                    title={`${wallet?.tradeable_count ?? 0} tradeable AEGIS · ${wallet?.tradeable_apt ?? 0} APT spendable`}
                    className="text-emerald-300/90"
                  >
                    ⚡ tradeable · <span className="text-emerald-300">{wallet?.tradeable_count ?? 0}</span> AEGIS
                  </span>
                  {wallet?.locked_count > 0 && (
                    <span className="text-sky-300/70" title="Locked coins (vault/treasury) — not transferable by their holder">
                      🔒 locked · <span className="text-sky-300">{wallet.locked_count}</span>
                    </span>
                  )}
                  {wallet?.vesting_locked_count > 0 && (
                    <span
                      className="text-sky-300/70 inline-flex items-center gap-1"
                      title={`${wallet.vesting_locked_count} verifier-payout coin${wallet.vesting_locked_count === 1 ? "" : "s"} still inside the ${wallet.vesting_cliff_days ?? 60}-day cliff. Worth ${(wallet.vesting_locked_apt ?? 0).toFixed(2)} APT; SPL withdraw unlocks ${formatVestingCountdown(wallet.next_vesting_unlock) ? "in " + formatVestingCountdown(wallet.next_vesting_unlock) : "soon"}. P2P transfers still work.`}
                      data-testid="wallet-vesting-summary"
                    >
                      <Clock className="h-3 w-3 inline" /> vesting ·{" "}
                      <span className="text-sky-300">{wallet.vesting_locked_count}</span>
                      {formatVestingCountdown(wallet.next_vesting_unlock) && (
                        <span className="text-zinc-500 normal-case">
                          {" "}(next in {formatVestingCountdown(wallet.next_vesting_unlock)})
                        </span>
                      )}
                    </span>
                  )}
                  <span data-testid="wallet-coin-count">
                    △ facts · <span className="text-amber-300">{wallet?.balance ?? 0}</span>
                  </span>
                  <span>discovered · <span className="text-emerald-300">{wallet?.recent?.discovered ?? 0}</span></span>
                  <span>received · <span className="text-sky-300">{wallet?.recent?.received ?? 0}</span></span>
                  <span>truth · <span className="text-zinc-300">{breakdown.truth}</span></span>
                  <span>refuted · <span className="text-zinc-300">{breakdown.refuted}</span></span>
                  <span>scope · <span className="text-zinc-300">{wallet?.scope || "self"}</span></span>
                </div>
              </div>
            </div>
          </div>
          <div
            className={`border p-4 ${
              !chain
                ? "border-zinc-800 bg-zinc-950/40"
                : chain?.chain?.valid
                ? "border-emerald-500/40 bg-emerald-500/5"
                : "border-rose-500/50 bg-rose-500/10"
            }`}
            data-testid="wallet-chain-status"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1 flex items-center gap-1.5">
              <ShieldCheck className="h-3 w-3" /> Chain integrity
            </div>
            {chain ? (
              <>
                <div className={`font-mono text-xl ${chain.chain.valid ? "text-emerald-300" : "text-rose-300"}`}>
                  {chain.chain.valid ? "✓ VERIFIED" : "✗ BROKEN"}
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-1 font-mono">
                  {chain.coin_count} coin{chain.coin_count === 1 ? "" : "s"} · {chain.chain.reason}
                </div>
                {!chain.chain.valid && isArchitect && (
                  <button
                    type="button"
                    onClick={repairChain}
                    disabled={repairingChain}
                    data-testid="wallet-repair-chain-btn"
                    title="Re-sign every broken prev_coin_hash link under the architect's HMAC secret. Original mint payload preserved exactly."
                    className="mt-3 inline-flex items-center gap-2 px-3 py-2 border border-rose-500 text-rose-300 hover:bg-rose-500 hover:text-zinc-950 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-[10px] uppercase tracking-[0.3em]"
                  >
                    <Wrench className={`h-3.5 w-3.5 ${repairingChain ? "animate-pulse" : ""}`} />
                    {repairingChain ? "Re-signing…" : "Repair chain"}
                  </button>
                )}
              </>
            ) : (
              <div className="font-mono text-xl text-zinc-500">Verifying…</div>
            )}
          </div>
        </div>

        {/* Send / Withdraw · collapsible actions (open on tap) */}
        <div className="grid sm:grid-cols-2 gap-3 mb-6" data-testid="wallet-actions-strip">
          <button
            type="button"
            onClick={() => setQuickSendOpen((s) => !s)}
            data-testid="wallet-action-send-toggle"
            className={`flex items-center justify-between px-4 py-3 text-[11px] uppercase tracking-[0.25em] border ${
              quickSendOpen
                ? "border-amber-500/60 text-amber-300 bg-amber-500/5"
                : "border-zinc-800 text-zinc-300 hover:border-amber-500/50 hover:text-amber-300"
            } transition-colors`}
          >
            <span className="inline-flex items-center gap-2">
              <Zap className="h-3.5 w-3.5" />
              Send AEGIS
            </span>
            <span className="text-[9px] tracking-[0.3em] text-zinc-500">
              {quickSendOpen ? "close" : "open"}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setWithdrawOpen((s) => !s)}
            data-testid="wallet-action-withdraw-toggle"
            className={`flex items-center justify-between px-4 py-3 text-[11px] uppercase tracking-[0.25em] border ${
              withdrawOpen
                ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/5"
                : "border-zinc-800 text-zinc-300 hover:border-emerald-500/50 hover:text-emerald-300"
            } transition-colors`}
          >
            <span className="inline-flex items-center gap-2">
              <ArrowUpRight className="h-3.5 w-3.5" />
              Withdraw to Solana
            </span>
            <span className="text-[9px] tracking-[0.3em] text-zinc-500">
              {withdrawOpen ? "close" : "open"}
            </span>
          </button>
        </div>
        {quickSendOpen && (
          <div className="mb-6" data-testid="wallet-action-send-panel">
            <QuickSendCard
              wallet={wallet}
              isArchitect={isArchitect}
              onSent={() => setRefreshTick((t) => t + 1)}
            />
          </div>
        )}
        {withdrawOpen && (
          <div className="mb-6" data-testid="wallet-action-withdraw-panel">
            <SolanaWithdrawCard
              wallet={wallet}
              onSent={() => setRefreshTick((t) => t + 1)}
            />
          </div>
        )}

        {/* Global supply stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-6">
          <div className="border border-amber-500/40 p-3 bg-amber-500/5" data-testid="stat-total-apt" title="Total APT minted on the chain — the scarce tradeable token. 1 APT = 1,000 PROOF (saved LLM tokens)">
            <div className="text-[9px] uppercase tracking-[0.3em] text-amber-400 flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> APT minted
            </div>
            <div className="font-mono text-2xl text-amber-300 mt-1">{supply ? formatApt(supply.total_minted_apt || 0) : "…"}</div>
            <div className="text-[8.5px] uppercase tracking-[0.25em] text-zinc-500 mt-0.5">scarce token</div>
          </div>
          <div className="border border-zinc-800 p-3 bg-[#0b0b0d]" data-testid="stat-total-proof" title="Saved LLM tokens — backing every APT 1:1000">
            <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-1">
              <TrendingUp className="h-3 w-3" /> PROOF saved
            </div>
            <div className="font-mono text-2xl text-amber-200/80 mt-1">{supply ? formatProof(supply.total_proof || 0) : "…"}</div>
            <div className="text-[8.5px] uppercase tracking-[0.25em] text-zinc-500 mt-0.5">backing supply</div>
          </div>
          <div className="border border-zinc-800 p-3 bg-[#0b0b0d]" data-testid="stat-total-supply">
            <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500">Facts minted</div>
            <div className="font-mono text-2xl text-amber-300 mt-1">{supply?.total_minted ?? "…"}</div>
            <div className="text-[8.5px] uppercase tracking-[0.25em] text-zinc-500 mt-0.5">AEGIS receipts</div>
          </div>
          <div className="border border-zinc-800 p-3 bg-[#0b0b0d]" data-testid="stat-truth-minted">
            <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500">Truth-minted</div>
            <div className="font-mono text-2xl text-emerald-300 mt-1">{supply?.by_type?.truth ?? "…"}</div>
          </div>
          <div className="border border-zinc-800 p-3 bg-[#0b0b0d]" data-testid="stat-holders">
            <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500">Distinct holders</div>
            <div className="font-mono text-2xl text-sky-300 mt-1">{supply?.distinct_holders ?? "…"}</div>
          </div>
        </div>

        {/* Transfers history (toggle) */}
        {showTransfers && (
          <div className="mb-6" data-testid="transfers-section">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2">
              <Inbox className="h-3 w-3" /> Transfer history · newest first
            </div>
            {(transfers?.transfers || []).length === 0 ? (
              <div className="border border-zinc-800 bg-[#0b0b0d] p-6 text-center text-[11px] uppercase tracking-[0.25em] text-zinc-500" data-testid="transfers-empty">
                No transfers yet
              </div>
            ) : (
              <div className="space-y-2">
                {transfers.transfers.map((t) => (
                  <TransferRow key={t.id} transfer={t} currentUserId={user?.user_id} isArchitect={!!user?.is_architect} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Coins list — collapsed into a slide-over "Receipts" panel so
            the wallet stays uncluttered for users with 100s of coins.
            The big-number summary above already says how much they own;
            this dialog is for the rare moments they want to inspect the
            individual HMAC receipts or pick specific coins to send. */}
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-2">
            <Layers className="h-3 w-3" /> Receipts ({wallet?.balance ?? 0} coins)
          </div>
          <div className="flex items-center gap-2">
            <Sheet>
              <SheetTrigger asChild>
                <button
                  type="button"
                  data-testid="open-receipts-drawer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 font-mono"
                >
                  <Hash className="h-3 w-3" /> view receipts
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-full sm:max-w-2xl bg-[#0b0b0d] border-zinc-800 text-zinc-100 overflow-y-auto"
                data-testid="receipts-drawer"
              >
                <SheetHeader>
                  <SheetTitle className="font-heading tracking-[0.1em] text-zinc-100 flex items-center gap-2">
                    <Hash className="h-4 w-4 text-amber-400" />
                    AEGIS Receipts
                  </SheetTitle>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
                    {wallet?.balance ?? 0} coins · each is one HMAC-signed verified-fact receipt
                  </div>
                </SheetHeader>
                <div className="mt-4 space-y-2">
                  {coins.length === 0 && (
                    <div className="text-zinc-500 text-xs uppercase tracking-[0.25em] font-mono p-6 text-center border border-dashed border-zinc-800">
                      No receipts yet
                    </div>
                  )}
                  {coins.map((c, i) => (
                    <CoinRow
                      key={c.id}
                      coin={c}
                      index={i}
                      selected={selected.includes(c.id)}
                      onToggle={toggleSelect}
                      currentUserId={user?.user_id}
                      isArchitect={!!user?.is_architect}
                    />
                  ))}
                </div>
                {selected.length > 0 && (
                  <div className="mt-6">
                    <SendPanel
                      selectedIds={selected}
                      onClear={() => setSelected([])}
                      onSent={() => setRefreshTick((t) => t + 1)}
                    />
                  </div>
                )}
              </SheetContent>
            </Sheet>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/aegis/verify`}
              target="_blank"
              rel="noreferrer noopener"
              className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-amber-300 border border-zinc-800 hover:border-amber-500/50 px-2 py-1 inline-flex items-center gap-1"
              data-testid="raw-chain-verify"
            >
              <ExternalLink className="h-3 w-3" /> raw chain JSON
            </a>
          </div>
        </div>

        {loading && !wallet && (
          <div className="text-zinc-500 text-sm py-12 text-center font-mono uppercase tracking-[0.25em]" data-testid="wallet-loading">
            loading chain…
          </div>
        )}
        {error && (
          <div className="text-rose-400 text-sm py-6 border border-rose-500/40 bg-rose-500/5 px-4 font-mono" data-testid="wallet-error">
            {String(error)}
          </div>
        )}
        {!loading && !error && coins.length === 0 && (
          <div className="text-zinc-500 text-sm py-16 text-center border border-zinc-800 bg-[#0b0b0d]" data-testid="wallet-empty">
            <div className="font-heading text-lg text-zinc-300 mb-2">No AEGIS in this wallet</div>
            <p className="text-xs uppercase tracking-[0.25em]">
              Verify a fact → seal a block → earn your first coin. Or have someone send you one.
            </p>
          </div>
        )}
        {coins.length > 0 && (
          <div
            className="border border-dashed border-zinc-800 bg-[#0b0b0d] p-6 text-center text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-mono"
            data-testid="receipts-collapsed-hint"
          >
            <Layers className="h-4 w-4 inline mr-2 opacity-50" />
            {wallet?.balance ?? 0} signed receipts · tap{" "}
            <span className="text-amber-300">view receipts</span> above to inspect or send specific coins
          </div>
        )}

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-zinc-900 text-[10px] uppercase tracking-[0.3em] text-zinc-600 leading-loose">
          <div>{supply?.notes}</div>
          <div className="mt-1">
            Mint ledger + transfer ledger are independently HMAC-chained · auditors can
            reconstruct any coin&apos;s full ownership history from Genesis · same anchor as the Truth Block chain.
          </div>
        </div>
      </div>
    </div>
  );
}
